import { Currency, getCurrencySymbol } from './constants/Currency';
import { MetricUnit, MetricDecimalPlaces } from './utils/metrics';

export function metricsFixedDecimal(
  value: number | undefined,
  field: keyof typeof MetricUnit,
  numDecimalPoints = 2,
  showPercent = true,
): string {
  if (typeof value !== 'number') return '-';

  const decimalPlaces = MetricDecimalPlaces[field] ?? numDecimalPoints;
  const unit = MetricUnit[field];
  if (unit === '%') {
    return `${preciseValue(value * 100).toFixed(decimalPlaces)}${!showPercent ? '' : unit}`;
  }

  if (value !== null) {
    const formatted = value.toLocaleString(undefined, {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    });

    // Non-% units (e.g. M / K) get a space; % is concatenated above without one.
    return unit === '' ? formatted : `${formatted} ${unit}`;
  }

  return '';
}

export function numDecimalPoints(
  value: string | number | null | undefined,
  {
    numDecimalPoints = 2,
    percent = false,
    /**
     * Use shouldShowPlainZero if you want to show "0" instead of "0.00"
     */
    shouldShowPlainZero = false,
  }: { numDecimalPoints?: number; percent?: boolean; shouldShowPlainZero?: boolean } = {},
): string {
  if (value == null) return '';

  if (typeof value === 'string') {
    const float = parseFloat(value);
    if (isNaN(float)) return value;

    value = float;
  }

  if (percent) {
    return `${preciseValue(value * 100).toFixed(numDecimalPoints)}%`;
  }

  if (shouldShowPlainZero && Math.abs(parseFloat(value.toFixed(numDecimalPoints))) === 0) {
    return '0';
  }

  return value.toFixed(numDecimalPoints);
}

export function units(
  value: number | string | undefined | null,
  unit: string,
  /**
   * @param currency - The currency to use for the currency symbol
   * @param numDecimalPoints - Number of decimal points to display
   */
  options: { currency?: Currency; numDecimalPoints?: number } = {},
): string {
  if (typeof value === 'undefined' || value === null) return '-';

  if (typeof value === 'string') return value;

  const percentage = Math.round((value + Number.EPSILON) * 1000) / 1000;
  const currency = Math.round(value);
  if (unit.startsWith('%')) return `${percentage.toLocaleString()}%`;

  if (unit.startsWith('$')) {
    const ccy = options?.currency ?? Currency.USD;
    return `${getCurrencySymbol(ccy)} ${currency.toLocaleString()}`;
  }

  // rounds to the number of decimal points specified
  if (options.numDecimalPoints) {
    return `${value.toLocaleString(undefined, {
      minimumFractionDigits: options.numDecimalPoints,
      maximumFractionDigits: options.numDecimalPoints,
    })}`;
  }

  return `${value.toLocaleString()}`;
}

export function displayStaticDatum(
  value: string | number | null | undefined,
  numberDisplayOption?: {
    numDecimalPoints?: number;
    percent?: boolean;
    /**
     * Use `shouldShowPlainZero` to show plain 0 when the value is 0, or if the value that would be displayed after rounding to `numDecimalPoints` (which defaults to 2) would show all 0s
     * e.g., this will show 0 instead of 0.00 or 0.001 (if `numDecimalPoints` is 2)
     */
    shouldShowPlainZero?: boolean;
    /** When null or omitted, thousand separators are applied. Only explicit false disables them. */
    useSeparators?: boolean;
  },
): string {
  if (value == null) return '-';

  // 2000.4567 → "2,000.46", 80 → "80.00%", 0.00 → "0", 1234 -> 1,234.00 %
  if (typeof value === 'number') {
    const decimalPlaces = numberDisplayOption?.numDecimalPoints ?? 2;
    const useSeparators = numberDisplayOption?.useSeparators !== false;

    const formatWithCommas = (val: number) => {
      if (!useSeparators) {
        return val.toFixed(decimalPlaces);
      }

      return val.toLocaleString(undefined, {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
      });
    };

    // if we would show all zeros with the given `numDecimalPlaces`, just show 0
    if (numberDisplayOption?.shouldShowPlainZero && Math.abs(value) < Math.pow(10, -decimalPlaces)) {
      return '0';
    }

    if (numberDisplayOption?.percent) {
      return `${formatWithCommas(value)}%`;
    }

    return formatWithCommas(value);
  }

  return value.toLocaleString();
}

/**
 * Remove extra decimal points after a single round of multiplication or division.
 * e.g. 100.31000000000002 -> 100.31
 *
 * Format a number to precision of 15 digits and then parseFloat it.
 * See: https://stackoverflow.com/a/3644302/923437 and https://stackoverflow.com/a/28047413/923437.
 */
export function preciseValue(value: number): number {
  return parseFloat(value.toPrecision(15));
}
